import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Play,
  PlusCircle,
  Video,
  Trash2,
  Mic,
  AlertCircle,
  Search,
  Filter,
  Edit,
  FolderKanban,
  FileText,
  SlidersHorizontal,
  X,
  Sparkles,
  Layers,
  ArrowUpDown,
  UploadCloud,
  RotateCcw,
  CheckSquare,
  Square,
  Download,
  CheckCircle2,
  HardDrive,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { EduModal } from '../components/EduModal.js';
import { EduConfirmDialog } from '../components/EduConfirmDialog.js';
import { BatchImportModal } from '../components/BatchImportModal.js';
import { BatchRenderModal } from '../components/BatchRenderModal.js';
import { api } from '../services/api.js';
import { Lesson, Project, DownloadJob } from '../types/index.js';

export const Lessons: React.FC = () => {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get('projectId') || '';
  const navigate = useNavigate();

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modais de Criação & Edição
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

  // Seleção Múltipla para Renderização e Download
  const [selectedLessonIds, setSelectedLessonIds] = useState<Set<string>>(new Set());
  const [isBatchRenderModalOpen, setIsBatchRenderModalOpen] = useState(false);

  // Downloads State
  const [downloadingLessonId, setDownloadingLessonId] = useState<string | null>(null);
  const [isZipDownloadModalOpen, setIsZipDownloadModalOpen] = useState(false);
  const [downloadingBatchZip, setDownloadingBatchZip] = useState(false);
  const [activeDownloadJob, setActiveDownloadJob] = useState<DownloadJob | null>(null);

  // Form State
  const [formProjectId, setFormProjectId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formOrder, setFormOrder] = useState<number>(1);
  const [formVoiceId, setFormVoiceId] = useState('faber');
  const [formRenderProfile, setFormRenderProfile] = useState('720p');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [deleteConfirmLesson, setDeleteConfirmLesson] = useState<Lesson | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // YouTube Transfer Status Map per Lesson
  const [youtubeJobMap, setYoutubeJobMap] = useState<
    Record<string, { videoId?: string; videoUrl?: string; status: string; errorMessage?: string }>
  >({});

  // Carregar Projetos
  const loadProjects = async () => {
    try {
      setProjectsLoading(true);
      const res = await api.getProjects();
      setProjects(res.items || []);
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Carregar Aulas
  const loadLessons = async () => {
    try {
      setLoading(true);
      const res = selectedProjectId
        ? await api.getProjectLessons(selectedProjectId)
        : await api.getLessons();
      setLessons(res.items || []);

      // If viewing a project, query recent YouTube transfer batch
      if (selectedProjectId) {
        try {
          const ytRes = await api.getYouTubeTransferBatches(undefined, 10);
          const relevantBatch = (ytRes.items || []).find((b) =>
            b.projectIds?.includes(selectedProjectId)
          );
          if (relevantBatch) {
            const batchDetails = await api.getYouTubeTransferBatch(relevantBatch.id);
            const map: Record<string, any> = {};
            batchDetails.jobs?.forEach((j) => {
              map[j.lessonId] = {
                videoId: j.youtubeVideoId,
                videoUrl: j.youtubeVideoUrl,
                status: j.status,
                errorMessage: j.errorMessage,
              };
            });
            setYoutubeJobMap(map);
          }
        } catch {}
      }
    } catch (err) {
      console.error('Erro ao carregar aulas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadLessons();
  }, [selectedProjectId]);

  // Alterar filtro de projeto na URL
  const handleProjectFilterChange = (projId: string) => {
    if (projId) {
      setSearchParams({ projectId: projId });
    } else {
      setSearchParams({});
    }
  };

  // Abrir Modal de Criação Rápida
  const openCreateModal = () => {
    setFormProjectId(selectedProjectId || (projects[0]?.id ?? ''));
    setFormTitle('');
    setFormDescription('');
    setFormOrder(lessons.length + 1);
    setFormVoiceId('faber');
    setFormRenderProfile('720p');
    setIsCreateModalOpen(true);
  };

  // Abrir Modal de Edição
  const openEditModal = (lesson: Lesson) => {
    setEditingLesson(lesson);
    setFormProjectId(lesson.projectId);
    setFormTitle(lesson.title);
    setFormDescription(lesson.description || '');
    setFormOrder(lesson.order || 1);
    setFormVoiceId(lesson.voiceId || 'faber');
    setFormRenderProfile(lesson.renderProfile || '720p');
  };

  // Salvar Criação ou Edição
  const handleSaveLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    setFormSubmitting(true);
    try {
      if (editingLesson) {
        await api.updateLesson(editingLesson.id, {
          title: formTitle.trim(),
          description: formDescription.trim() || undefined,
          order: Number(formOrder),
          voiceId: formVoiceId,
          renderProfile: formRenderProfile,
        });
        setEditingLesson(null);
      } else {
        if (!formProjectId) {
          alert('Selecione um projeto para a nova aula.');
          return;
        }
        await api.createLesson(formProjectId, {
          title: formTitle.trim(),
          description: formDescription.trim() || undefined,
          order: Number(formOrder),
          voiceId: formVoiceId,
          renderProfile: formRenderProfile,
        });
        setIsCreateModalOpen(false);
      }
      loadLessons();
      loadProjects(); // Atualizar contadores
    } catch (err: any) {
      alert(err.message || 'Erro ao salvar aula.');
    } finally {
      setFormSubmitting(false);
    }
  };


  // Ação de Excluir
  const handleConfirmDelete = async () => {
    if (!deleteConfirmLesson) return;

    try {
      setDeleteLoading(true);
      await api.deleteLesson(deleteConfirmLesson.id);
      setDeleteConfirmLesson(null);
      loadLessons();
      loadProjects();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir aula.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filtragem
  const filteredLessons = useMemo(() => {
    return lessons.filter((l) => {
      const matchesSearch =
        l.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.description && l.description.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === 'all' || l.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [lessons, searchTerm, statusFilter]);

  const selectedLessonsList = useMemo(() => {
    return lessons.filter((l) => selectedLessonIds.has(l.id));
  }, [lessons, selectedLessonIds]);

  const toggleSelectAll = () => {
    const selectable = filteredLessons.filter((l) => l.status !== 'rendering');
    if (selectedLessonIds.size >= selectable.length && selectable.length > 0) {
      setSelectedLessonIds(new Set());
    } else {
      setSelectedLessonIds(new Set(selectable.map((l) => l.id)));
    }
  };

  const toggleSelectLesson = (id: string) => {
    const next = new Set(selectedLessonIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedLessonIds(next);
  };

  // Download Individual de Videoaula MP4
  const handleDownloadSingleLesson = async (lesson: Lesson) => {
    try {
      setDownloadingLessonId(lesson.id);
      const safeTitle = (lesson.title || `Aula ${lesson.order}`).replace(/[<>:"/\\|?*]/g, '');
      const num = lesson.order < 10 ? `0${lesson.order}` : `${lesson.order}`;
      const fileName = `Aula ${num} - ${safeTitle}.mp4`;
      await api.downloadLessonMp4(lesson.id, fileName);
    } catch (err: any) {
      alert(err.message || 'Falha ao baixar videoaula.');
    } finally {
      setDownloadingLessonId(null);
    }
  };

  // Download em Lote das Aulas Selecionadas (ZIP)
  const handleDownloadSelectedZip = async () => {
    const selectedCompleted = lessons.filter(
      (l) => selectedLessonIds.has(l.id) && l.status === 'completed'
    );

    if (selectedCompleted.length === 0) {
      alert('Nenhuma das aulas selecionadas possui vídeo MP4 finalizado para download.');
      return;
    }

    setDownloadingBatchZip(true);
    try {
      const job = await api.createBatchDownload(
        selectedCompleted.map((l) => l.id),
        selectedProjectId || undefined
      );
      setActiveDownloadJob(job);
      setIsZipDownloadModalOpen(true);
      pollDownloadJob(job.id);
    } catch (err: any) {
      alert(err.message || 'Falha ao iniciar empacotamento das aulas em ZIP.');
    } finally {
      setDownloadingBatchZip(false);
    }
  };

  // Download de Todos os Vídeos do Projeto Ativo (ZIP)
  const handleDownloadAllProjectVideos = async () => {
    if (!selectedProjectId) return;
    setDownloadingBatchZip(true);
    try {
      const res = await api.createProjectDownload(selectedProjectId);
      setActiveDownloadJob(res.job);
      setIsZipDownloadModalOpen(true);
      pollDownloadJob(res.job.id);
    } catch (err: any) {
      alert(err.message || 'Falha ao iniciar empacotamento do projeto em ZIP.');
    } finally {
      setDownloadingBatchZip(false);
    }
  };

  const pollDownloadJob = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const job = await api.getDownloadJob(jobId);
        setActiveDownloadJob(job);
        if (job.status === 'ready' || job.status === 'error' || job.status === 'expired') {
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, 2000);
  };

  const currentProjectName = useMemo(() => {
    if (!selectedProjectId) return null;
    return projects.find((p) => p.id === selectedProjectId)?.name || 'Projeto Selecionado';
  }, [projects, selectedProjectId]);

  const getStatusBadge = (lesson: Lesson) => {
    switch (lesson.status) {
      case 'completed':
        return <EduBadge variant="success" dot>Pronta</EduBadge>;
      case 'rendering':
        return <EduBadge variant="orange" dot>Renderizando</EduBadge>;
      case 'ready':
        return <EduBadge variant="info" dot>Pronta p/ Render</EduBadge>;
      case 'error':
        return <EduBadge variant="error" dot>Erro</EduBadge>;
      default:
        return (lesson.sceneCount || 0) > 0 ? (
          <EduBadge variant="neutral" dot>Importada</EduBadge>
        ) : (
          <EduBadge variant="neutral" dot>Rascunho</EduBadge>
        );
    }
  };

  return (
    <div>
      <Header
        title="Minhas Aulas"
        subtitle={
          currentProjectName
            ? `Aulas vinculadas ao projeto: ${currentProjectName}`
            : 'Gerencie todas as videoaulas, cenas e status de renderização dos seus projetos'
        }
        action={
          <div className="flex items-center gap-2.5">
            <EduButton
              variant="secondary"
              size="sm"
              onClick={() => setIsBatchModalOpen(true)}
              icon={<UploadCloud size={15} className="text-brand-primary" />}
            >
              Importar Aulas (ZIP)
            </EduButton>
            <EduButton
              variant="secondary"
              size="sm"
              onClick={openCreateModal}
              icon={<PlusCircle size={15} />}
            >
              Criar Aula
            </EduButton>
            <EduButton
              variant="primary"
              size="sm"
              onClick={() =>
                navigate(
                  selectedProjectId
                    ? `/nova-videoaula?projectId=${selectedProjectId}`
                    : '/nova-videoaula'
                )
              }
              icon={<Sparkles size={15} />}
            >
              Assistente IA
            </EduButton>
          </div>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Barra Superior: Seletor de Projeto, Busca e Filtro de Status */}
        <div className="bg-surface border border-bdr rounded-xl p-4 shadow-sm flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
            {/* Seletor de Projeto */}
            <div className="flex items-center gap-2">
              <FolderKanban size={16} className="text-brand-primary shrink-0" />
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectFilterChange(e.target.value)}
                className="bg-surface-secondary border border-bdr rounded-lg px-3 py-2 text-xs font-semibold text-txt-primary focus:outline-none focus:border-brand-primary min-w-[200px]"
              >
                <option value="">Todos os Projetos ({projects.length})</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.lessonCount || 0} aulas)
                  </option>
                ))}
              </select>
            </div>

            {/* Input de Busca */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-2.5 text-txt-muted" size={15} />
              <input
                type="text"
                placeholder="Buscar por título ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-xs bg-surface-secondary border border-bdr rounded-lg text-txt-primary placeholder:text-txt-muted focus:outline-none focus:border-brand-primary focus:bg-surface transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-2.5 text-txt-muted hover:text-txt-primary"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
            {/* Ações em Lote quando houver seleção */}
            {selectedLessonIds.size > 0 ? (
              <div className="flex items-center gap-2 bg-surface-secondary px-3 py-1.5 rounded-lg border border-bdr">
                <span className="text-xs font-bold text-txt-primary">
                  {selectedLessonIds.size} {selectedLessonIds.size === 1 ? 'selecionada' : 'selecionadas'}
                </span>
                <EduButton
                  variant="primary"
                  size="sm"
                  onClick={() => setIsBatchRenderModalOpen(true)}
                  icon={<Video size={13} />}
                >
                  Renderizar ({selectedLessonIds.size})
                </EduButton>
                <EduButton
                  variant="secondary"
                  size="sm"
                  onClick={handleDownloadSelectedZip}
                  disabled={downloadingBatchZip}
                  icon={<Download size={13} className="text-brand-primary" />}
                  title="Baixar aulas selecionadas em um arquivo compactado ZIP"
                >
                  {downloadingBatchZip ? 'Empacotando...' : 'Baixar ZIP'}
                </EduButton>
                <button
                  onClick={() => setSelectedLessonIds(new Set())}
                  className="text-xs text-txt-muted hover:text-txt-primary underline px-1"
                >
                  Limpar
                </button>
              </div>
            ) : selectedProjectId && (
              <EduButton
                variant="secondary"
                size="sm"
                onClick={handleDownloadAllProjectVideos}
                disabled={downloadingBatchZip}
                icon={<Download size={13} className="text-brand-primary" />}
                title="Empacotar e baixar todos os vídeos finalizados deste projeto em formato ZIP"
              >
                {downloadingBatchZip ? 'Preparando...' : 'Baixar Todos os Vídeos (ZIP)'}
              </EduButton>
            )}

            {/* Filtro de Status */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-txt-secondary font-medium">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-surface border border-bdr rounded-lg px-3 py-1.5 text-xs text-txt-primary focus:outline-none focus:border-brand-primary"
              >
                <option value="all">Todos os Status</option>
                <option value="draft">Rascunhos</option>
                <option value="ready">Prontas p/ Render</option>
                <option value="rendering">Renderizando</option>
                <option value="completed">Prontas / Concluídas</option>
                <option value="error">Com Erro</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabela de Aulas */}
        {loading ? (
          <div className="p-16 text-center text-xs text-txt-muted flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <span>Carregando videoaulas...</span>
          </div>
        ) : filteredLessons.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <Video size={40} className="mx-auto text-txt-muted/60 mb-3" />
              <h3 className="text-base font-semibold text-txt-primary">Nenhuma aula encontrada</h3>
              <p className="text-xs text-txt-secondary mt-1 max-w-md mx-auto">
                {searchTerm || statusFilter !== 'all'
                  ? 'Nenhuma aula encontrada com os filtros informados. Experimente limpar os filtros.'
                  : 'Este projeto ainda não possui aulas cadastradas. Crie sua primeira aula ou use o assistente de importação para gerar cenas automaticamente.'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <div className="mt-5 flex items-center justify-center gap-3">
                  <EduButton
                    variant="secondary"
                    size="sm"
                    onClick={openCreateModal}
                    icon={<PlusCircle size={14} />}
                  >
                    Criar Aula Manual
                  </EduButton>
                  <EduButton
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      navigate(
                        selectedProjectId
                          ? `/nova-videoaula?projectId=${selectedProjectId}`
                          : '/nova-videoaula'
                      )
                    }
                    icon={<Sparkles size={14} />}
                  >
                    Importar Material c/ IA
                  </EduButton>
                </div>
              )}
            </div>
          </EduCard>
        ) : (
          <div className="bg-surface border border-bdr rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-surface-secondary border-b border-bdr text-txt-secondary font-semibold">
                    <th className="py-3.5 px-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={
                          filteredLessons.length > 0 &&
                          selectedLessonIds.size > 0 &&
                          selectedLessonIds.size >= filteredLessons.filter((l) => l.status !== 'rendering').length
                        }
                        onChange={toggleSelectAll}
                        className="rounded border-bdr text-brand-primary focus:ring-brand-primary cursor-pointer w-4 h-4"
                        title="Selecionar todas as aulas visíveis"
                      />
                    </th>
                    <th className="py-3.5 px-3 w-14 text-center">#</th>
                    <th className="py-3.5 px-4">Aula</th>
                    <th className="py-3.5 px-4">Cenas</th>
                    <th className="py-3.5 px-4">Voz Piper</th>
                    <th className="py-3.5 px-4">Qualidade</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">Duração</th>
                    <th className="py-3.5 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bdr text-txt-primary">
                  {filteredLessons.map((lesson) => {
                    const isRendering = lesson.status === 'rendering';
                    const isCompleted = lesson.status === 'completed';
                    const isError = lesson.status === 'error';
                    const hasScenes = (lesson.sceneCount || 0) > 0;
                    const isSelected = selectedLessonIds.has(lesson.id);

                    return (
                      <tr
                        key={lesson.id}
                        className={`hover:bg-surface-secondary/40 transition-colors group ${
                          isSelected ? 'bg-orange-50/30' : ''
                        }`}
                      >
                        <td className="py-3.5 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectLesson(lesson.id)}
                            disabled={isRendering}
                            className="rounded border-bdr text-brand-primary focus:ring-brand-primary cursor-pointer w-4 h-4 disabled:opacity-30"
                            title={isRendering ? 'Aula em renderização' : 'Selecionar para renderização em lote'}
                          />
                        </td>
                        <td className="py-3.5 px-3 text-center font-mono font-bold text-txt-muted">
                          #{lesson.order}
                        </td>
                        <td className="py-3.5 px-4 max-w-sm">
                          <span className="font-semibold block text-txt-primary text-sm">
                            {lesson.title}
                          </span>
                          {lesson.description && (
                            <span className="text-[11px] text-txt-secondary line-clamp-1 mt-0.5">
                              {lesson.description}
                            </span>
                          )}
                          {youtubeJobMap[lesson.id] && (
                            <div className="mt-1 flex items-center gap-1.5">
                              {youtubeJobMap[lesson.id].status === 'completed' && youtubeJobMap[lesson.id].videoId ? (
                                <a
                                  href={`https://www.youtube.com/watch?v=${youtubeJobMap[lesson.id].videoId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
                                  title="Assistir vídeo no canal do YouTube"
                                >
                                  <svg className="w-3 h-3 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                  </svg>
                                  YouTube <ExternalLink size={9} />
                                </a>
                              ) : ['uploading', 'processing_youtube', 'adding_to_playlist'].includes(youtubeJobMap[lesson.id].status) ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-status-info border border-blue-200">
                                  <RefreshCw size={9} className="animate-spin" /> Enviando YT
                                </span>
                              ) : youtubeJobMap[lesson.id].status === 'failed' ? (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-status-error border border-red-200"
                                  title={youtubeJobMap[lesson.id].errorMessage || 'Falha na transferência ao YouTube'}
                                >
                                  <AlertCircle size={9} /> Erro YouTube
                                </span>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td className="py-3.5 px-4 font-medium">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-surface-secondary border border-bdr text-txt-secondary font-semibold">
                            <Layers size={12} className="text-brand-primary" />
                            {lesson.sceneCount || 0} cenas
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center gap-1 font-medium capitalize text-txt-secondary">
                            <Mic size={13} className="text-brand-primary" />
                            {lesson.voiceId || 'faber'}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="px-2 py-0.5 rounded text-[11px] font-mono font-semibold bg-slate-100 text-txt-secondary border border-slate-200">
                            {lesson.renderProfile || '720p'}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          {getStatusBadge(lesson)}
                        </td>
                        <td className="py-3.5 px-4 font-mono text-txt-secondary">
                          {lesson.durationSeconds
                            ? `${Math.floor(lesson.durationSeconds / 60)}:${(
                                lesson.durationSeconds % 60
                              )
                                .toString()
                                .padStart(2, '0')}`
                            : '—'}
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Botão Abrir no Editor Visual */}
                            <EduButton
                              variant="secondary"
                              size="sm"
                              onClick={() => navigate(`/aulas/${lesson.id}/editor`)}
                              icon={<Layers size={13} className="text-brand-primary" />}
                              title="Abrir Editor de Cenas em Carrossel"
                            >
                              Editar Cenas
                            </EduButton>

                            {isCompleted ? (
                              <div className="flex items-center gap-1">
                                {/* Botão Download Individual MP4 */}
                                <EduButton
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleDownloadSingleLesson(lesson)}
                                  disabled={downloadingLessonId === lesson.id}
                                  icon={<Download size={12} className="text-brand-primary" />}
                                  title="Baixar arquivo de vídeo MP4 desta aula diretamente"
                                >
                                  {downloadingLessonId === lesson.id ? 'Baixando...' : 'Download MP4'}
                                </EduButton>

                                <EduButton
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => navigate('/biblioteca')}
                                  icon={<Play size={12} className="text-status-success fill-status-success" />}
                                  title="Assistir vídeo gerado"
                                >
                                  Assistir
                                </EduButton>
                                <EduButton
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedLessonIds(new Set([lesson.id]));
                                    setIsBatchRenderModalOpen(true);
                                  }}
                                  icon={<RotateCcw size={12} className="text-brand-primary" />}
                                  title="Reprocessar e gerar um novo vídeo MP4 desta aula"
                                >
                                  Reprocessar
                                </EduButton>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                {/* Botão Download Desabilitado com Mensagem Explicativa */}
                                <button
                                  disabled
                                  className="px-2 py-1 rounded text-[11px] font-medium text-txt-muted bg-surface-secondary border border-bdr/60 cursor-not-allowed opacity-50 inline-flex items-center gap-1"
                                  title="Esta aula ainda não possui vídeo MP4 finalizado para download. Inicie a renderização para gerar."
                                >
                                  <Download size={11} /> Sem Vídeo
                                </button>

                                <EduButton
                                  variant={hasScenes ? 'primary' : 'secondary'}
                                  size="sm"
                                  onClick={() => {
                                    if (!hasScenes) {
                                      alert(
                                        'Esta aula ainda não possui cenas cadastradas. Clique em "Editar Cenas" ou use a tela de Nova Videoaula para cadastrar o conteúdo.'
                                      );
                                      return;
                                    }
                                    setSelectedLessonIds(new Set([lesson.id]));
                                    setIsBatchRenderModalOpen(true);
                                  }}
                                  disabled={isRendering}
                                  icon={isError ? <RotateCcw size={12} /> : <Video size={12} />}
                                  title={isError ? 'Tentar renderização novamente' : 'Iniciar produção da videoaula'}
                                >
                                  {isRendering ? 'Renderizando...' : isError ? 'Reprocessar' : 'Renderizar'}
                                </EduButton>
                              </div>
                            )}

                            {/* Botão Editar Dados da Aula */}
                            <button
                              onClick={() => openEditModal(lesson)}
                              className="p-1.5 text-txt-muted hover:text-brand-primary hover:bg-orange-50 rounded-lg transition-colors"
                              title="Editar Informações da Aula"
                            >
                              <Edit size={14} />
                            </button>

                            {/* Botão Excluir */}
                            <button
                              onClick={() => setDeleteConfirmLesson(lesson)}
                              className="p-1.5 text-txt-muted hover:text-status-error hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir Aula"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Rodapé informativo */}
            <div className="px-6 py-3 bg-surface-secondary/60 border-t border-bdr text-xs text-txt-secondary flex items-center justify-between">
              <span>
                Total de <strong>{filteredLessons.length}</strong> {filteredLessons.length === 1 ? 'aula' : 'aulas'} exibidas
              </span>
              <span className="text-[11px] text-txt-muted">
                Dica: Cada renderização concluída consome 1 aula da sua cota mensal contratada.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Criação / Edição de Aula */}
      <EduModal
        isOpen={isCreateModalOpen || !!editingLesson}
        onClose={() => {
          if (!formSubmitting) {
            setIsCreateModalOpen(false);
            setEditingLesson(null);
          }
        }}
        title={editingLesson ? 'Editar Videoaula' : 'Nova Videoaula'}
        subtitle={
          editingLesson
            ? `Atualize os metadados da aula #${editingLesson.order}`
            : 'Cadastre os detalhes da nova aula para organizar suas cenas'
        }
        maxWidth="md"
      >
        <form onSubmit={handleSaveLesson} className="space-y-4">
          {!editingLesson && (
            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Projeto Vinculado <span className="text-status-error">*</span>
              </label>
              <select
                required
                value={formProjectId}
                onChange={(e) => setFormProjectId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface"
              >
                <option value="">Selecione o projeto...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1">
              Título da Aula <span className="text-status-error">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="Ex: Introdução aos Direitos Fundamentais"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1">
              Descrição / Ementa (Opcional)
            </label>
            <textarea
              rows={2}
              placeholder="Tópicos abordados, observações para o professor..."
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Ordem da Aula
              </label>
              <input
                type="number"
                min={1}
                value={formOrder}
                onChange={(e) => setFormOrder(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Voz Piper TTS
              </label>
              <select
                value={formVoiceId}
                onChange={(e) => setFormVoiceId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface capitalize"
              >
                <option value="faber">Faber (Padrão PT-BR)</option>
                <option value="edresson">Edresson</option>
                <option value="leticia">Letícia</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-txt-primary mb-1">
                Qualidade
              </label>
              <select
                value={formRenderProfile}
                onChange={(e) => setFormRenderProfile(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary focus:outline-none focus:border-brand-primary bg-surface font-mono"
              >
                <option value="720p">720p HD</option>
                <option value="1080p">1080p Full HD</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2.5 pt-4 border-t border-bdr">
            <EduButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingLesson(null);
              }}
              disabled={formSubmitting}
            >
              Cancelar
            </EduButton>
            <EduButton
              type="submit"
              variant="primary"
              size="sm"
              isLoading={formSubmitting}
            >
              {editingLesson ? 'Salvar Alterações' : 'Criar Aula'}
            </EduButton>
          </div>
        </form>
      </EduModal>

      {/* Barra Flutuante de Ação em Lote */}
      {selectedLessonIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-slate-700/80 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs font-bold font-mono">
              {selectedLessonIds.size}
            </span>
            <span className="text-sm font-medium text-slate-200">
              {selectedLessonIds.size === 1
                ? '1 aula selecionada'
                : `${selectedLessonIds.size} aulas selecionadas`}
            </span>
          </div>

          <div className="h-4 w-px bg-slate-700" />

          <EduButton
            variant="primary"
            size="sm"
            onClick={() => setIsBatchRenderModalOpen(true)}
            icon={<Video size={14} />}
          >
            Renderizar Selecionadas ({selectedLessonIds.size})
          </EduButton>

          <button
            onClick={() => setSelectedLessonIds(new Set())}
            className="text-xs text-slate-400 hover:text-white transition-colors underline ml-1"
          >
            Limpar seleção
          </button>
        </div>
      )}

      {/* Modal de Renderização em Lote / Reprocessamento com Franquia */}
      <BatchRenderModal
        isOpen={isBatchRenderModalOpen}
        onClose={() => setIsBatchRenderModalOpen(false)}
        selectedLessons={selectedLessonsList}
        onSuccess={() => {
          setSelectedLessonIds(new Set());
          loadLessons();
        }}
      />

      {/* Diálogo de Confirmação para Excluir Aula */}
      <EduConfirmDialog
        isOpen={!!deleteConfirmLesson}
        onClose={() => setDeleteConfirmLesson(null)}
        onConfirm={handleConfirmDelete}
        title="Excluir Aula"
        description={
          <span>
            Tem certeza que deseja excluir a aula{' '}
            <strong>"{deleteConfirmLesson?.title}"</strong>? Todas as cenas e áudios
            associados serão removidos.
          </span>
        }
        confirmLabel="Sim, excluir aula"
        cancelLabel="Cancelar"
        variant="danger"
        isLoading={deleteLoading}
      />

      {/* Modal de Download ZIP das Aulas */}
      <EduModal
        isOpen={isZipDownloadModalOpen}
        onClose={() => !downloadingBatchZip && setIsZipDownloadModalOpen(false)}
        title="Download de Aulas em Lote (ZIP)"
        subtitle="Empacotamento assíncrono dos arquivos de vídeo MP4"
        maxWidth="sm"
      >
        <div className="space-y-4 text-center py-2">
          {activeDownloadJob?.status === 'ready' ? (
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-50 text-status-success mx-auto flex items-center justify-center">
                <CheckCircle2 size={24} />
              </div>
              <h4 className="text-sm font-bold text-txt-primary">Pacote ZIP Pronto!</h4>
              <p className="text-xs text-txt-secondary">
                O arquivo compactado contém <strong>{activeDownloadJob.fileCount} videoaulas</strong> prontas para download.
              </p>
              <div className="pt-2">
                <a
                  href={api.getDownloadJobFileUrl(activeDownloadJob.id)}
                  download
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-brand-primary text-white font-bold text-sm hover:bg-brand-hover shadow-sm transition-colors"
                >
                  <Download size={16} /> Baixar Pacote ZIP
                </a>
              </div>
            </div>
          ) : activeDownloadJob?.status === 'error' ? (
            <div className="space-y-2">
              <AlertCircle size={24} className="text-status-error mx-auto" />
              <h4 className="text-xs font-bold text-status-error">Erro ao Gerar Pacote</h4>
              <p className="text-xs text-txt-muted">{activeDownloadJob.errorMessage || 'Falha no empacotamento.'}</p>
            </div>
          ) : (
            <div className="space-y-3 py-4">
              <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <h4 className="text-xs font-bold text-txt-primary">Preparando Pacote ZIP no Servidor...</h4>
              <p className="text-[11px] text-txt-secondary max-w-xs mx-auto leading-relaxed">
                Empacotando as videoaulas MP4 em segundo plano. Não consome franquia e não reconverte os vídeos.
              </p>
            </div>
          )}
        </div>
      </EduModal>
    </div>
  );
};
